import { IdCard, Megaphone, Swords } from "lucide-react";

/**
 * KindGlyph: one abstract, monochrome stroke mark per signal kind.
 *
 * The de-coloured kind doctrine holds: kinds are NEVER colour-coded (the six-hex
 * brand palette does not encode a rainbow). Differentiation is carried by FORM
 * instead: stroke-only marks in currentColor, so they inherit ink and work in
 * both themes. The seven kinds a person can log have their own drawn marks; the
 * three the field reporter files on its own (identity, competitive pattern,
 * active campaign) borrow lucide marks at the same stroke weight. Anything else
 * gets the neutral ring.
 */

const STROKE = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

const GLYPHS: Record<string, React.ReactNode> = {
  // Rising staircase + head: something is surging.
  trend: (
    <>
      <path d="M3 19h4v-4h4v-4h4V7h4" />
      <path d="M15.5 4.5H19V8" />
    </>
  ),
  // A waveform changing register mid-line.
  tone_shift: (
    <>
      <path d="M3 12c1.6 0 1.6-3 3.2-3s1.6 3 3.2 3" />
      <path d="M12 12c1.5 0 1.5-6 3-6s1.5 12 3 12" opacity="0.9" />
      <path d="M20 12h1" />
    </>
  ),
  // A position abandoning one point for another.
  positioning_shift: (
    <>
      <circle cx="6.5" cy="15.5" r="2.5" opacity="0.45" />
      <circle cx="17" cy="8" r="3.5" />
      <path d="M9 13.5 13.5 10" />
    </>
  ),
  // Content leaving one frame for another.
  platform_shift: (
    <>
      <rect x="3" y="5" width="8" height="8" opacity="0.45" />
      <rect x="13" y="11" width="8" height="8" />
      <path d="M9 15.5h2.5" />
    </>
  ),
  // A burst: the moment, radiating.
  cultural_moment: (
    <>
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4" />
      <path d="M5.9 5.9l2.5 2.5M15.6 15.6l2.5 2.5M18.1 5.9l-2.5 2.5M8.4 15.6l-2.5 2.5" opacity="0.55" />
    </>
  ),
  // The middle column breaking rank.
  category_shift: (
    <>
      <path d="M4.5 20V13" opacity="0.45" />
      <path d="M12 20V6" />
      <path d="M19.5 20v-9" opacity="0.45" />
      <path d="M9.5 8.5 12 6l2.5 2.5" />
    </>
  ),
  // The crowd re-centering around a new point.
  audience_shift: (
    <>
      <circle cx="7" cy="9" r="2" opacity="0.45" />
      <circle cx="12.5" cy="6.5" r="2" opacity="0.45" />
      <circle cx="16.5" cy="14.5" r="3" />
      <path d="M5 17c.4-2 1.8-3.2 3.6-3.6" opacity="0.45" />
    </>
  ),
};

/** The reporter-only kinds, drawn with lucide at the house stroke weight. */
const LUCIDE_GLYPHS: Record<string, typeof IdCard> = {
  identity: IdCard,
  competitive_pattern: Swords,
  active_campaign: Megaphone,
};

export default function KindGlyph({
  kind,
  size = 18,
  className,
}: {
  kind: string;
  size?: number;
  className?: string;
}) {
  const Lucide = LUCIDE_GLYPHS[kind];
  if (Lucide) {
    return (
      <Lucide
        width={size}
        height={size}
        strokeWidth={STROKE.strokeWidth}
        aria-hidden="true"
        className={className}
      />
    );
  }

  const mark = GLYPHS[kind] ?? <circle cx="12" cy="12" r="6" />;
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden="true"
      className={className}
      {...STROKE}
    >
      {mark}
    </svg>
  );
}
